"use client";

import { useEffect, useState } from "react";
import { defaultLocale, type Locale } from "./config";
import { getLocaleFromPathname } from "./locale";

/**
 * Detects the active locale from `window.location.pathname` after mount.
 *
 * For client components that render outside next-intl / router context —
 * error boundaries (`error.tsx`, `global-error.tsx`) and the root
 * `not-found.tsx` boundary — and therefore cannot read the `[locale]` route
 * param directly. `global-error.tsx` in particular replaces the entire app
 * (no parent layout, no next-intl provider), so `window.location` is the
 * only signal available.
 *
 * First paint returns `defaultLocale` to match the server render and avoid a
 * hydration mismatch; corrected synchronously in an effect right after mount
 * (same trade-off `NotFoundContent`'s inline `detectLocale()` already makes).
 */
export function useClientLocale(): Locale {
	const [locale, setLocale] = useState<Locale>(defaultLocale);

	useEffect(() => {
		setLocale(getLocaleFromPathname(window.location.pathname));
	}, []);

	return locale;
}
